import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Map, Loader2, Calendar, Target, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const VisualRoadmap = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [roadmap, setRoadmap] = useState(null);

  const tier = user?.subscription_tier || 'free';
  const roadmapTypeLabel = tier === 'pro' ? '12-Month' : tier === 'premium' ? 'Quarterly' : '30-Day';

  useEffect(() => {
    fetchLatestRoadmap();
  }, []);

  const fetchLatestRoadmap = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/roadmap/latest`, { headers });
      if (response.data.roadmap) {
        setRoadmap(response.data.roadmap);
      }
    } catch (error) {
      console.error('Error fetching roadmap:', error);
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/roadmap/generate`, {}, { headers });
      setRoadmap(response.data);
      toast.success('Visual roadmap generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate roadmap');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout title="Visual Strategy Roadmap">
      <div className="max-w-6xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-premium p-8 mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <Map className="text-[#E5C585] mb-4" size={28} />
              <h2 className="font-serif text-2xl text-white mb-2">{roadmapTypeLabel} Visual Roadmap</h2>
              <p className="text-[#A1A1AA]">Get a strategic plan for your brand's visual journey.</p>
            </div>
            <Button onClick={handleGenerate} disabled={loading} className="btn-gold px-8 py-4 flex items-center gap-2" data-testid="generate-roadmap-btn">
              {loading ? (<><Loader2 className="animate-spin" size={18} />Generating...</>) : roadmap ? 'Regenerate Roadmap' : 'Generate Roadmap'}
            </Button>
          </div>
        </motion.div>

        {roadmap && (
          <div className="space-y-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card-premium p-8">
              <h3 className="font-serif text-xl text-white mb-4">Strategy Overview</h3>
              <p className="text-[#A1A1AA] text-lg leading-relaxed">{roadmap.overview}</p>
            </motion.div>

            {/* Phases */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
              <h3 className="font-serif text-2xl text-white mb-6">Phases</h3>
              <div className="space-y-6">
                {roadmap.phases?.map((phase, i) => (
                  <div key={i} className="card-premium p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#E5C585] to-[#D4AF37] flex items-center justify-center flex-shrink-0">
                        <span className="text-black font-serif text-xl">{phase.phase_number}</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-serif text-xl text-white">{phase.title}</h4>
                          <span className="text-[#52525B] text-sm">{phase.duration}</span>
                        </div>
                        <p className="text-[#E5C585] text-sm mb-4">{phase.focus}</p>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <span className="text-[#52525B] text-xs font-accent tracking-wider">CONTENT THEMES</span>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {phase.content_themes?.map((theme, j) => (
                                <span key={j} className="px-3 py-1 bg-[#1C1C1C] text-[#A1A1AA] text-sm">{theme}</span>
                              ))}
                            </div>
                          </div>
                          <div>
                            <span className="text-[#52525B] text-xs font-accent tracking-wider">MARKETING ACTIONS</span>
                            <ul className="mt-2 space-y-1">
                              {phase.marketing_actions?.map((action, j) => (
                                <li key={j} className="text-[#A1A1AA] text-sm flex items-center gap-2">
                                  <CheckCircle size={12} className="text-[#D4AF37]" />{action}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                        <p className="text-[#52525B] text-sm mt-4 italic">{phase.visual_direction}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Content Calendar */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <h3 className="font-serif text-2xl text-white mb-6">Monthly Content Calendar</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {roadmap.monthly_content_calendar?.map((week, i) => (
                  <div key={i} className="card-premium p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Calendar size={16} className="text-[#E5C585]" />
                      <span className="text-white font-serif">Week {week.week}</span>
                    </div>
                    <p className="text-[#E5C585] text-xs mb-2">{week.focus_theme}</p>
                    <ul className="space-y-1">
                      {week.posts?.map((post, j) => (
                        <li key={j} className="text-[#A1A1AA] text-sm">• {post}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Milestones */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="card-premium p-8">
              <div className="flex items-center gap-3 mb-6">
                <Target className="text-[#D4AF37]" size={24} />
                <h3 className="font-serif text-xl text-white">Key Milestones</h3>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                {roadmap.key_milestones?.map((milestone, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 bg-[#1C1C1C] border border-[#27272A]">
                    <CheckCircle className="text-[#D4AF37] flex-shrink-0 mt-1" size={18} />
                    <span className="text-[#A1A1AA]">{milestone}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        )}

        {!roadmap && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card-premium p-16 text-center">
            <Map className="text-[#27272A] mx-auto mb-6" size={80} />
            <h3 className="font-serif text-2xl text-white mb-4">No Roadmap Generated Yet</h3>
            <p className="text-[#A1A1AA] max-w-md mx-auto">Click the button above to create your personalized visual strategy roadmap.</p>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default VisualRoadmap;
